import type { SelectOption } from './select';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { Checkbox } from '../checkbox';
import { TextInput } from '../text-input';

import styles from './select.module.css';

import { Dropdown } from '../dropdown';

export interface SelectMenuProps {
  isOpen: boolean;
  onClose?: () => void;
  closeOnChange?: boolean;
  value?: SelectOption | SelectOption[];
  options?: SelectOption[];
  optionsIdProperty: string;
  getOptionsLabel: (option: SelectOption) => string;
  className?: string;
  isSearchable?: boolean;
  isMultiselect?: boolean;
  forceMenuWidth?: boolean;
  testId?: string;
  searchPlaceholder?: string;
  onChange?: (option: SelectOption | SelectOption[]) => void;
  ref: React.RefObject<HTMLElement | null>;
}

const MOBILE_BREAKPOINT = '(max-width: 768px)';
const OPTIONS_LIST_LIMIT = 100;
const MAX_HEIGHT = 270;

export const SelectMenu = ({
  isOpen,
  onClose,
  closeOnChange = true,
  value = undefined,
  options = [],
  optionsIdProperty,
  getOptionsLabel,
  className,
  isSearchable,
  isMultiselect,
  forceMenuWidth,
  testId = 'select-menu',
  searchPlaceholder,
  onChange,
  ref,
}: SelectMenuProps) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_BREAKPOINT).matches : false
  );
  const [search, setSearch] = useState('');
  const limitOptions = useCallback(
    (options: SelectOption[]) => (isSearchable && !search ? options.slice(0, OPTIONS_LIST_LIMIT) : options),
    [isSearchable, search]
  );
  const [displayOptions, setDisplayOptions] = useState(limitOptions(options));
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeMenu = () => {
    setSearch('');
    onClose && onClose();
  };

  const selectOption = (option: SelectOption) => {
    onChange?.(option);
    closeOnChange && closeMenu();
  };

  const toggleOption = (option: SelectOption) => {
    if (Array.isArray(value)) {
      const id = option[optionsIdProperty];
      const newValue = isOptionSelected(option)
        ? [...value.filter((selectedValues) => selectedValues[optionsIdProperty] !== id)]
        : [...value, option];

      onChange?.(newValue);
    }
  };

  const isOptionSelected = (option: SelectOption) => {
    return Array.isArray(value)
      ? value.some((val) => val[optionsIdProperty] === option[optionsIdProperty])
      : value && value[optionsIdProperty] === option[optionsIdProperty];
  };

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(media.matches);

    media.addEventListener('change', onChange);

    return () => media.removeEventListener('change', onChange);
  }, []);

  useLayoutEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (isSearchable && isOpen) {
      // Timeout is needed to ensure that the dropdown is open before focusing
      timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(
    function filterOptions() {
      const searchText = search.toLowerCase().trim();

      const filteredOptions = options.filter((option) => {
        return (
          getOptionsLabel(option)?.toLowerCase().includes(searchText) ||
          String(option[optionsIdProperty])?.toLowerCase().includes(searchText)
        );
      });

      setDisplayOptions(limitOptions(filteredOptions));
    },
    [options, search, limitOptions, optionsIdProperty, getOptionsLabel]
  );

  return (
    <Dropdown
      isOpen={isOpen}
      testId={testId}
      referenceElement={ref?.current}
      className={classNames(styles.items, className)}
      maxHeight={MAX_HEIGHT}
      forceWidthAsReference={forceMenuWidth}
      handleClose={closeMenu}
    >
      {isSearchable && (
        <TextInput
          data-testid={`${testId}-search`}
          placeholder={searchPlaceholder || t('select.search.placeholder')}
          type="text"
          className={styles.search}
          value={search}
          ref={searchInputRef}
          onChange={(event) => setSearch(event.target.value)}
        >
          <SearchIcon />
        </TextInput>
      )}
      {isMultiselect &&
        displayOptions.map((option) => (
          <Checkbox
            key={option[optionsIdProperty]}
            data-testid={`${testId}-option-${option[optionsIdProperty]}`}
            className={styles.item}
            checked={isOptionSelected(option)}
            onChange={() => toggleOption(option)}
            disabled={option?.disabled}
          >
            <div className={styles.itemTextWrapper}>
              {getOptionsLabel(option)}
              {option.hint && <div className={styles.optionsHint}>{option.hint}</div>}
            </div>
          </Checkbox>
        ))}
      {!isMobile &&
        !isMultiselect &&
        displayOptions.map((option) => (
          <button
            key={option[optionsIdProperty]}
            type="button"
            data-testid={`${testId}-option-${option[optionsIdProperty]}`}
            className={classNames(
              styles.item,
              isOptionSelected(option) && styles.selected,
              option.hint && styles.optionsHintAvailable,
              option?.disabled && styles.disabled
            )}
            onClick={() => selectOption(option)}
            aria-disabled={option?.disabled}
          >
            <div className={styles.itemTextWrapper}>
              {getOptionsLabel(option)}
              {option.hint && (
                <div data-testid={`${testId}-option-${option[optionsIdProperty]}-hint`} className={styles.optionsHint}>
                  {option.hint}
                </div>
              )}
            </div>
          </button>
        ))}
      {displayOptions.length === 0 && (
        <div className={classNames(styles.item, styles.empty)}>{t('select.search.noResults')}</div>
      )}
    </Dropdown>
  );
};
